import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LexdexLogo } from '../components/LexdexLogo';
import './ResetPassword.css';

const API_URL = import.meta.env.VITE_API_URL || 'https://1v6zm22cmf.execute-api.us-west-2.amazonaws.com/prod';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = API_URL.endsWith('/') ? API_URL : `${API_URL}/`;
      await axios.post(`${apiUrl}auth/confirm-reset`, {
        token,
        newPassword,
      });
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-logo-container">
          <LexdexLogo className="reset-password-logo" showText={false} />
        </div>

        <h1 className="reset-password-title">Set New Password</h1>

        {!success ? (
          <>
            <p className="reset-password-description">
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit} className="reset-password-form">
              {error && (
                <div className="reset-password-error">
                  {error}
                </div>
              )}

              <div className="reset-password-input-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  disabled={loading || !token}
                  minLength={8}
                />
              </div>

              <div className="reset-password-input-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  disabled={loading || !token}
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                className="reset-password-button"
                disabled={loading || !token}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>

            <div className="reset-password-back">
              <Link to="/login">← Back to Login</Link>
            </div>
          </>
        ) : (
          <div className="reset-password-success">
            <div className="success-icon">✓</div>
            <h2>Password Reset Successfully</h2>
            <p>
              Your password has been reset successfully. You can now log in with your new password.
            </p>
            <p className="success-note">
              Redirecting to login page...
            </p>
            <Link to="/login" className="reset-password-button">
              Go to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;

